import { useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * BackButtonHandler - Handles the native Back button on mobile devices
 * 
 * Functionality:
 * - When the user opens a modal/overlay, it is added to history
 * - When the user presses the Back button on mobile, the modal closes instead of navigating back
 * - Works with any modal: services, gallery, menu, etc.
 */

// Store for active modals
let activeModals = [];
let modalCloseCallbacks = {};

// Public API for components
export const registerModal = (modalId, closeCallback) => {
  if (!activeModals.includes(modalId)) {
    activeModals.push(modalId);
    modalCloseCallbacks[modalId] = closeCallback;
    
    // Push state so the back button can close the modal
    window.history.pushState({ modal: modalId, timestamp: Date.now() }, '', window.location.href);
  }
};

export const unregisterModal = (modalId) => {
  const index = activeModals.indexOf(modalId);
  if (index > -1) {
    activeModals.splice(index, 1);
    delete modalCloseCallbacks[modalId];
  }
};

export const hasActiveModals = () => activeModals.length > 0;

export default function BackButtonHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  const handlePopState = useCallback((event) => {
    // If there are active modals, close the last one
    if (activeModals.length > 0) {
      const lastModalId = activeModals[activeModals.length - 1];
      const closeCallback = modalCloseCallbacks[lastModalId];
      
      if (closeCallback) {
        closeCallback();
        
        // Remove the modal from the list
        unregisterModal(lastModalId);
        
        // Prevent navigation - stay on the same page
        event.preventDefault();
        return;
      }
    }
    
    // If no active modals, allow normal navigation
  }, []);

  useEffect(() => {
    // Listen for the popstate event (Back button)
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  // Reset modals on route change + scroll to top
  useEffect(() => {
    activeModals = [];
    modalCloseCallbacks = {};
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Renders nothing - just a handler
  return null;
}

/**
 * Custom hook to manage a modal with back-button support
 * 
 * Usage:
 * const { openModal, closeModal, isOpen } = useModalWithBackButton('service-detail');
 * 
 * openModal(); // Opens and registers for the back button
 * closeModal(); // Closes and removes from history
 */
export function useModalWithBackButton(modalId) {
  const openModal = useCallback((customCloseCallback) => {
    registerModal(modalId, customCloseCallback);
  }, [modalId]);

  const closeModal = useCallback(() => {
    unregisterModal(modalId);
    
    // Go back in history if we added a state
    if (window.history.state?.modal === modalId) {
      window.history.back();
    }
  }, [modalId]);

  // Cleanup la unmount
  useEffect(() => {
    return () => {
      unregisterModal(modalId);
    };
  }, [modalId]);

  return { openModal, closeModal };
}
